import { useState, useRef, useEffect } from 'react';
import { createPost, fetchOgData } from '../services/api';
import LinkPreview from './LinkPreview';
import EmojiPicker from './EmojiPicker';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'video/mp4', 'video/webm', 'video/quicktime'];
const MAX_FILES = 5;
const MAX_SIZE_MB = 50;
const URL_REGEX = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/i;
function extractFirstUrl(text) {
  const m = URL_REGEX.exec(text);
  if (!m) return null;
  const raw = m[0].replace(/[.,!?)]$/, '');
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

export default function CreatePost({ onPostCreated, selectedCountry = '' }) {
  const [content, setContent] = useState('');
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const fileRef = useRef();
  const textRef = useRef();
  const [ogData, setOgData] = useState(null);
  const ogUrlRef = useRef(null);

  // Debounced link preview fetch
  useEffect(() => {
    const url = extractFirstUrl(content);
    if (!url) {
      ogUrlRef.current = null;
      setOgData(null);
      return;
    }
    if (!url || url === ogUrlRef.current) return;
    ogUrlRef.current = url;
    const timer = setTimeout(() => {
      fetchOgData(url)
        .then(setOgData)
        .catch(() => setOgData(null));
    }, 600);
    return () => clearTimeout(timer);
  }, [content]);

  function insertEmoji(emoji) {
    const el = textRef.current;
    if (!el) {
      setContent((prev) => `${prev}${emoji}`);
      return;
    }

    const start = el.selectionStart ?? content.length;
    const end = el.selectionEnd ?? content.length;
    const next = `${content.slice(0, start)}${emoji}${content.slice(end)}`;
    setContent(next);

    requestAnimationFrame(() => {
      el.focus();
      const cursor = start + emoji.length;
      el.setSelectionRange(cursor, cursor);
    });
  }

  function handleFiles(selected) {
    const valid = [];
    const newPreviews = [];

    for (const file of selected) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError(`"${file.name}" is not a supported type (jpg, png, mp4, webm, mov).`);
        return;
      }
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        setError(`"${file.name}" exceeds ${MAX_SIZE_MB} MB.`);
        return;
      }
      valid.push(file);
      newPreviews.push({ url: URL.createObjectURL(file), type: file.type, name: file.name });
    }

    if (files.length + valid.length > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} files per post.`);
      return;
    }

    setError('');
    setFiles((prev) => [...prev, ...valid]);
    setPreviews((prev) => [...prev, ...newPreviews]);
  }

  function removeFile(idx) {
    URL.revokeObjectURL(previews[idx].url);
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!content.trim()) {
      setError('Post content cannot be empty.');
      return;
    }

    setLoading(true);
    setUploadProgress(0);
    setError('');

    const fd = new FormData();
    fd.append('content', content.trim());
    if (selectedCountry) fd.append('country', selectedCountry);
    files.forEach((f) => fd.append('media', f));

    try {
      const post = await createPost(fd, {
        onProgress: (pct) => setUploadProgress(pct),
      });
      setContent('');
      setFiles([]);
      previews.forEach((p) => URL.revokeObjectURL(p.url));
      setPreviews([]);
      setOpen(false);
      onPostCreated && onPostCreated(post);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  }

  if (!open) {
    return (
      <div className="create-post-toggle">
        <button className="btn-primary" onClick={() => setOpen(true)}>
          New Anonymous Post
        </button>
      </div>
    );
  }

  return (
    <div className="create-post-card">
      <div className="create-post-header">
        <h2>New Post</h2>
        <button className="btn-ghost" onClick={() => setOpen(false)}>✕</button>
      </div>

      <p className="anon-notice">
        🔒 Fully anonymous — no account, no tracking, no IP logging.
        A random username will be assigned automatically.
      </p>

      <form onSubmit={handleSubmit}>
        <textarea
          ref={textRef}
          className="post-textarea custom-textarea"
          placeholder="Share a report, story, or tip anonymously…"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          maxLength={5000}
          rows={6}
          disabled={loading}
        />
        <EmojiPicker onSelect={insertEmoji} disabled={loading} />
        <div className="char-count">{content.length} / 5000</div>

        {/* File upload */}
        <div className="upload-area">
          <button
            type="button"
            className="btn-ghost"
            onClick={() => fileRef.current.click()}
            disabled={loading || files.length >= MAX_FILES}
          >
            📎 Attach Images / Videos ({files.length}/{MAX_FILES})
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".jpg,.jpeg,.png,.mp4,.webm,.mov"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => handleFiles(Array.from(e.target.files))}
          />
        </div>

        {/* Preview */}
        {previews.length > 0 && (
          <div className="preview-grid">
            {previews.map((p, i) => (
              <div key={i} className="preview-item">
                {p.type.startsWith('image/') ? (
                  <img src={p.url} alt={p.name} />
                ) : (
                  <video src={p.url} controls />
                )}
                <button
                  type="button"
                  className="preview-remove"
                  onClick={() => removeFile(i)}
                  title="Remove"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {extractFirstUrl(content) && <LinkPreview og={ogData} url={extractFirstUrl(content)} />}

        {error && <p className="form-error">{error}</p>}

        {loading && files.length > 0 && (
          <div className="upload-progress" aria-live="polite">
            <div className="upload-progress-track">
              <div className="upload-progress-fill" style={{ width: `${uploadProgress}%` }} />
            </div>
            <span className="upload-progress-label">Uploading media... {uploadProgress}%</span>
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="btn-ghost"
            onClick={() => setOpen(false)}
            disabled={loading}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={loading || !content.trim()}>
            {loading ? 'Posting…' : '🚀 Post Anonymously'}
          </button>
        </div>
      </form>
    </div>
  );
}
